import { getDataFetcher, postDataFetcher } from '@/utils/apis';
import {
  getApprovalPermissionsUrl,
  APPROVAL_REFERRERS_URL,
} from '@/constant/approval';

/**
 * 품의 코멘트 멘션 권한체크 + 미권한자 자동 참조인 등록 API.
 *  - GET  /api/v2/approval/{documentId}/permissions?passportIds[]=a&passportIds[]=b
 *  - POST /api/v2/approval/{documentId}/referrers   body { passportIds, comment }
 */

/**
 * 권한체크 응답에서 미권한자 passportId 집합을 뽑는다.
 *
 * 응답: `{ data: { "<passportId>": { permission: boolean, ... } } }`
 * (data 는 passportId 맵, `permission === false` 가 미권한자)
 *
 * 형태가 다르면 빈 Set 반환(fail-open) — 권한체크 실패가 코멘트 등록을 막지 않는다.
 */
const pickUnauthorized = (body: unknown): Set<string> => {
  const result = new Set<string>();

  const root =
    body && typeof body === 'object' && 'data' in (body as object)
      ? (body as { data: unknown }).data
      : body;

  if (!root || typeof root !== 'object' || Array.isArray(root)) {
    return result;
  }

  for (const [passportId, detail] of Object.entries(
    root as Record<string, unknown>,
  )) {
    if (
      detail &&
      typeof detail === 'object' &&
      (detail as { permission?: unknown }).permission === false
    ) {
      result.add(passportId);
    }
  }

  return result;
};

/**
 * 멘션된 passportIds 중 "문서 열람 권한이 없는" passportId 집합을 반환.
 * 빈 배열이면 네트워크 호출 없이 빈 Set.
 */
export const checkUnauthorizedPassports = async (
  documentId: string,
  passportIds: string[],
): Promise<Set<string>> => {
  if (passportIds.length === 0) return new Set();

  try {
    const res = await getDataFetcher(
      getApprovalPermissionsUrl(documentId, passportIds),
    );
    return pickUnauthorized(res);
  } catch {
    // 호출 실패 시 fail-open — 코멘트 등록은 막지 않는다.
    return new Set();
  }
};

/** 품의에 참조인을 사후 추가 (passportId 배열을 한 번에). */
export const addApprovalReferrers = async (
  documentId: string,
  passportIds: string[],
  comment = '',
): Promise<void> => {
  await postDataFetcher(APPROVAL_REFERRERS_URL(documentId), {
    passportIds,
    comment,
  });
};
